import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle, Check } from 'lucide-react'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { BackLink } from '@/components/navigation/BackLink'
import { PatientBanner } from '@/components/patient/PatientBanner'
import { formsApi } from '@/api/formsApi'
import { proceduresApi } from '@/api/proceduresApi'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { FormRenderer } from '@/components/renderer/FormRenderer'
import { formCrumbs, formReturn, hasPatient, recordLabel, type FormRun } from '@/lib/formPageRun'
import { patientNameFromSubmissions, referencedFormNames } from '@/lib/journey'
import { useTenant } from '@/providers/TenantProvider'
import {
  buildCrossFormSources,
  collectCrossFormRefs,
  resolveCrossRefs,
  type CrossFormResolution,
} from '@/lib/crossForm'
import { CGT_FORM_LABELS } from '@/types/cgt'
import type {
  FormDefinition,
  FormSubmission,
  CgtFormType,
  Procedure,
  ProcedureJourney,
} from '@/types/cgt'
import { useCurrentCgtUser } from '@/hooks/useCurrentCgtUser'
import { FORM_PAGE_GUTTER_CLASS } from '@/lib/pageLayout'
import { cn } from '@/lib/utils'


export function FormRendererPage() {
  const currentUser = useCurrentCgtUser()
  const { submissionId } = useParams<{ submissionId: string }>()
  const navigate = useNavigate()
  const [definition, setDefinition] = useState<FormDefinition | null>(null)
  const [submission, setSubmission] = useState<FormSubmission | null>(null)
  // The run the form is on: its type says whether `patientMrn` is a patient's.
  const [procedure, setProcedure] = useState<Procedure | null>(null)
  const [patientName, setPatientName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [justCompleted, setJustCompleted] = useState(false)
  // Set when THIS user just gave the review sign-off (z8nrz7dt41). A reviewer
  // came from their worklist, so that is where they go back to.
  const [justReviewed, setJustReviewed] = useState(false)
  const wasCompleted = useRef(false)
  const statusRef = useRef<FormSubmission['status'] | null>(null)
  // Journey + sibling data for resolving cross-form references ({handle.fieldId}).
  const [journey, setJourney] = useState<ProcedureJourney | null>(null)
  const [allDefs, setAllDefs] = useState<FormDefinition[]>([])
  const [patientSubs, setPatientSubs] = useState<FormSubmission[]>([])
  const { selectedTenant } = useTenant()

  useEffect(() => {
    if (!submissionId) return
    setLoading(true)
    formsApi
      .getSubmission(submissionId)
      .then(async sub => {
        const [def, subs, proc] = await Promise.all([
          formsApi.getDefinition(sub.formDefinitionId),
          // Per-patient; a submission without one has no siblings to fetch.
          sub.patientMrn ? formsApi.getSubmissionsForPatient(sub.patientMrn) : Promise.resolve([]),
          // Loaded with the form, not after it, so the header knows whether the
          // run has a patient before it renders. Non-fatal, like the journey.
          sub.procedureId
            ? proceduresApi.get(sub.procedureId).catch(e => {
                console.error('Failed to load the procedure', e)
                return null
              })
            : Promise.resolve(null),
        ])
        setSubmission(sub)
        setProcedure(proc)
        setDefinition(def)
        setPatientSubs(subs)
        // A form waiting for review is already done as far as its filler is
        // concerned, so it counts here: reviewing it is not "just completed".
        wasCompleted.current = sub.status === 'completed' || sub.status === 'pending_review'
        statusRef.current = sub.status
        // Cross-form refs resolve within the patient's journey — only forms on
        // a procedure have one. Failure to load it is non-fatal: refs
        // then evaluate as empty, same as an unfilled upstream form.
        let journey: ProcedureJourney | null = null
        if (proc && sub.patientMrn) {
          try {
            journey = await procedureTemplatesApi.journey(proc.procedureTemplateId, sub.patientMrn, proc.id)
          } catch (e) {
            console.error('Failed to load journey for cross-form refs', e)
          }
        }
        // Only the forms the patient's submissions and the journey reference,
        // every version of each — never the whole library, which is every
        // version of every form in full (z8nrz7e8kw).
        const defs = await formsApi.listDefinitionsByName(referencedFormNames(journey?.steps, subs))
        setAllDefs(defs)
        setPatientName(patientNameFromSubmissions(subs, defs))
        if (journey) setJourney(journey)
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load form.'))
      .finally(() => setLoading(false))
  }, [submissionId])

  // Same shared mechanism as the patient procedure page (lib/crossForm): namespaced
  // values from sibling journey steps, states for waiting/warning notes.
  const crossForm = useMemo<CrossFormResolution | undefined>(() => {
    if (!journey || !definition || !submission) return undefined
    const refs = collectCrossFormRefs(definition)
    if (refs.length === 0) return undefined
    const defsById = new Map(allDefs.map(d => [d.id, d]))
    const subsById = new Map(patientSubs.map(s => [s.id, s]))
    const sources = buildCrossFormSources(
      journey.steps
        .filter(s => s.formDefinitionId !== submission.formDefinitionId)
        .map(s => {
          const def = defsById.get(s.formDefinitionId)
          const sub = s.submissionId ? subsById.get(s.submissionId) : undefined
          return {
            formHandle: def?.handle ?? s.formHandle,
            formName: s.formName,
            definition: def ?? null,
            values: sub ? sub.values : null,
            // Every repeat skipped: what reads it is ignored (z8nrz7fn5r).
            skipped: s.status === 'skipped',
          }
        }),
    )
    return resolveCrossRefs(refs, sources)
  }, [journey, definition, submission, allDefs, patientSubs])

  const run: FormRun = {
    type: procedure?.procedureTemplate.type ?? null,
    procedureId: submission?.procedureId,
    patientMrn: submission?.patientMrn,
    mfgNumber: procedure?.mfgNumber,
  }
  const returnTo = formReturn(run)

  const handleUpdated = useCallback(
    (updated: FormSubmission) => {
      setSubmission(updated)
      const previous = statusRef.current
      statusRef.current = updated.status
      if (previous === 'pending_review' && updated.status === 'completed') {
        setJustReviewed(true)
        setTimeout(() => navigate('/my-steps'), 1100)
        return
      }
      if (
        (updated.status === 'completed' || updated.status === 'pending_review') &&
        !wasCompleted.current
      ) {
        wasCompleted.current = true
        setJustCompleted(true)
        // Back to the run's own page: a kit run, an MES order or the patient's
        // procedure. With nowhere to go, stay put and let the operator navigate
        // (z8nrz7ckj9).
        const back = formReturn({
          type: procedure?.procedureTemplate.type ?? null,
          procedureId: updated.procedureId,
          patientMrn: updated.patientMrn,
        })
        if (back) setTimeout(() => navigate(back.path), 1100)
      }
    },
    [navigate, procedure],
  )

  const formLabel = definition
    ? CGT_FORM_LABELS[definition.formType as CgtFormType] ?? definition.name
    : 'Form'

  return (
    <div className='min-h-full bg-muted/20'>
      {/* Only a patient's run has someone to show. A kit run keeps its supply
          id in patientMrn, and looking that up as a patient 404s. */}
      {submission?.patientMrn && hasPatient(run) && (
        <PatientBanner mrn={submission.patientMrn} displayName={patientName} />
      )}

      <div className={cn('w-full py-8', FORM_PAGE_GUTTER_CLASS)}>
        {/* Additive, not a replacement: the crumb below is a real hierarchy
            (Patients > MRN > Procedure, Kits > kit, MES > order), which a
            caller's origin should not overwrite. The back link just offers the
            way you actually came. */}
        <BackLink className='mb-2' />

        <Breadcrumbs items={formCrumbs(run, formLabel)} className='mb-5' />

        {loading && (
          <div className='flex items-center justify-center py-20'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {error && (
          <div className='flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {error}
          </div>
        )}

        {justReviewed && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800'>
            <Check className='h-4 w-4 shrink-0' />
            Review signed — returning to your review list…
          </div>
        )}
        {justCompleted && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800'>
            <Check className='h-4 w-4 shrink-0' />
            {submission?.status === 'pending_review' ? 'Form completed and sent for review' : 'Form completed'}
            {returnTo ? ` — returning to ${returnTo.label}…` : '.'}
          </div>
        )}

        {definition && submission && (
          <div className='rounded-xl border bg-card p-6'>
            <FormRenderer
              definition={definition}
              submission={submission}
              currentUser={currentUser}
              onSubmissionUpdated={handleUpdated}
              crossForm={crossForm}
              tenantTimezone={selectedTenant?.timezone}
              recordLabel={recordLabel(run)}
            />
          </div>
        )}
      </div>
    </div>
  )
}
